import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PackageFormValues } from '@/types/package';
import { formatRupiah } from '@/utils/format';
import { PackageFormErrors } from '../packageForm';
import {
  AddRowButton,
  FieldError,
  FieldHelper,
  FieldLabel,
  FormCard,
  inputClass,
  RupiahInput,
  ToggleRow,
} from './PackageFormControls';

interface PackageDepositSectionProps {
  values: PackageFormValues;
  errors: PackageFormErrors;
  onChange: <K extends keyof PackageFormValues>(
    field: K,
    value: PackageFormValues[K]
  ) => void;
}

export const PackageDepositSection: React.FC<PackageDepositSectionProps> = ({
  values,
  errors,
  onChange,
}) => {
  const updateFreeDay = (key: string, day: string) => {
    onChange(
      'freeDayDates',
      values.freeDayDates.map((entry) =>
        entry.key === key ? { ...entry, day } : entry
      )
    );
  };

  const addFreeDay = () => {
    onChange('freeDayDates', [
      ...values.freeDayDates,
      { key: uuidv4(), day: '' },
    ]);
  };

  const removeFreeDay = (key: string) => {
    onChange(
      'freeDayDates',
      values.freeDayDates.filter((entry) => entry.key !== key)
    );
  };

  return (
    <FormCard title="Deposit & Hari Gratis">
      <div className="px-5 py-4">
        <ToggleRow
          label="Deposit"
          helper="Jaminan yang dibayarkan penyewa di awal masa sewa."
          checked={values.depositEnabled}
          onChange={(checked) => onChange('depositEnabled', checked)}
        />

        {values.depositEnabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel required>Deposit Total (Rp)</FieldLabel>
              <RupiahInput
                value={values.deposit}
                onChange={(value) => onChange('deposit', value)}
                placeholder="500000"
                hasError={!!errors.deposit}
              />
              <FieldError>{errors.deposit}</FieldError>
            </div>
            <div>
              <FieldLabel hint="(Optional)">
                Deposit Non-Refundable (Rp)
              </FieldLabel>
              <RupiahInput
                value={values.depositNonRefundable}
                onChange={(value) => onChange('depositNonRefundable', value)}
                placeholder="250000"
                hasError={!!errors.depositNonRefundable}
              />
              <FieldHelper withIcon>
                Bagian deposit yang tidak dikembalikan. Sisanya (
                {formatRupiah(
                  Math.max(
                    Number(values.deposit || 0) -
                      Number(values.depositNonRefundable || 0),
                    0
                  ),
                  'Rp0',
                  { space: false }
                )}
                ) bersifat refundable.
              </FieldHelper>
              <FieldError>{errors.depositNonRefundable}</FieldError>
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-4 border-t border-gray-200">
        <ToggleRow
          label="Down Payment"
          helper="Pembayaran di muka sebelum cicilan sewa dimulai."
          checked={values.downPaymentEnabled}
          onChange={(checked) => onChange('downPaymentEnabled', checked)}
        />

        {values.downPaymentEnabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Down Payment (DP)</FieldLabel>
              <RupiahInput
                value={values.minDp}
                onChange={(value) => onChange('minDp', value)}
                placeholder="200000"
                hasError={!!errors.minDp}
              />
              <FieldHelper withIcon>
                Jumlah pembayaran di muka yang harus dibayar penyewa
              </FieldHelper>
              <FieldError>{errors.minDp}</FieldError>
            </div>
            <div>
              <FieldLabel>Cicilan Deposit</FieldLabel>
              <div
                className={`flex items-center border rounded-lg px-3 focus-within:ring-1 focus-within:ring-primary ${
                  errors.maxInstallments
                    ? 'border-red-500'
                    : 'border-gray-200'
                }`}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={values.maxInstallments}
                  onChange={(e) =>
                    onChange(
                      'maxInstallments',
                      e.target.value.replace(/\D/g, '')
                    )
                  }
                  placeholder="30"
                  aria-label="Cicilan Deposit"
                  className="flex-1 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
                />
                <span className="text-[14px] text-gray-400 ml-2">x</span>
              </div>
              <FieldHelper withIcon>
                Jumlah cicilan untuk melunasi deposit
              </FieldHelper>
              <FieldError>{errors.maxInstallments}</FieldError>
            </div>
          </div>
        )}
      </div>

      {/* Not in the design, but POST/PUT requires it and the instalment
          invariant (TRD C2) is validated against it. */}
      <div className="px-5 py-4 border-t border-gray-200">
        <FieldLabel required>Minimal Masa Sewa Kontrak (Hari)</FieldLabel>
        <input
          type="text"
          inputMode="numeric"
          value={values.minRentalDays}
          onChange={(e) =>
            onChange('minRentalDays', e.target.value.replace(/\D/g, ''))
          }
          aria-label="Minimal Masa Sewa Kontrak"
          className={inputClass(!!errors.minRentalDays)}
        />
        <FieldHelper withIcon>
          Penyewa tidak dapat mengembalikan unit sebelum jumlah hari ini
          (default 90). Cicilan deposit harus selesai dalam periode ini.
        </FieldHelper>
        <FieldError>{errors.minRentalDays}</FieldError>
      </div>

      <div className="px-5 py-4 border-t border-gray-200">
        <ToggleRow
          label="Hari Gratis"
          helper="Hari di mana penyewa tidak dikenakan biaya sewa (gratis)."
          checked={values.freeDaysEnabled}
          onChange={(checked) => onChange('freeDaysEnabled', checked)}
        />

        {values.freeDaysEnabled && (
          <>
            <div className="mb-4">
              <FieldLabel>Minimal Masa Sewa (Hari)</FieldLabel>
              <input
                type="text"
                inputMode="numeric"
                value={values.freeDayMinTenureDays}
                onChange={(e) =>
                  onChange('freeDayMinTenureDays', e.target.value.replace(/\D/g, ''))
                }
                aria-label="Minimal Masa Sewa"
                className={inputClass(!!errors.freeDayMinTenureDays)}
              />
              <FieldHelper>
                Minimal masa sewa sebelum eligible mendapatkan hari gratis
              </FieldHelper>
              <FieldError>{errors.freeDayMinTenureDays}</FieldError>
            </div>

            <FieldLabel>Pilih Tanggal</FieldLabel>
            <div className="space-y-2">
              {values.freeDayDates.map((entry) => (
                <div key={entry.key} className="flex items-center gap-2">
                  <div className="flex flex-1 items-center border border-gray-200 rounded-lg px-3 focus-within:ring-1 focus-within:ring-primary">
                    <i className="ri-calendar-line text-[18px] text-gray-400 mr-2"></i>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={entry.day}
                      onChange={(e) =>
                        updateFreeDay(
                          entry.key,
                          e.target.value.replace(/\D/g, '').slice(0, 2)
                        )
                      }
                      placeholder="Tanggal"
                      aria-label="Tanggal hari gratis"
                      className="flex-1 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFreeDay(entry.key)}
                    aria-label="Hapus tanggal"
                    className="w-11 h-11 flex items-center justify-center border border-red-400 rounded-lg text-red-500 hover:bg-red-50 shrink-0"
                  >
                    <i className="ri-delete-bin-line text-[18px]"></i>
                  </button>
                </div>
              ))}
            </div>
            <FieldError>{errors.freeDayDates}</FieldError>
            <div className="mt-3">
              <AddRowButton label="Tambah Hari" onClick={addFreeDay} />
            </div>
          </>
        )}
      </div>
    </FormCard>
  );
};

export default PackageDepositSection;
